import { useState, useEffect } from "react";
import axios from "axios";
import API_ENDPOINTS from "config/api";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import PropTypes from "prop-types";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";

export default function FlotasVehiculos() {
  const [vehiculos, setVehiculos] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editando, setEditando] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [vehiculoActual, setVehiculoActual] = useState({
    placa: "",
    tipo: "",
    marca: "",
    modelo: "",
    año: "",
    color: "",
    capacidad_carga: "",
    capacidad_volumen: "",
    kilometraje: "",
    estado: "activo",
    fecha_adquisicion: "",
    valor_adquisicion: "",
    ultimo_mantenimiento: "",
    proximo_mantenimiento: "",
    observaciones: "",
  });

  const tiposVehiculo = [
    { value: "camion", label: "Camión" },
    { value: "camioneta", label: "Camioneta" },
    { value: "van", label: "Van" },
    { value: "moto", label: "Moto" },
    { value: "trailer", label: "Trailer" },
  ];

  const estadosVehiculo = [
    { value: "activo", label: "Activo", color: "success" },
    { value: "mantenimiento", label: "Mantenimiento", color: "warning" },
    { value: "reparacion", label: "Reparación", color: "error" },
    { value: "inactivo", label: "Inactivo", color: "secondary" },
  ];

  useEffect(() => {
    cargarVehiculos();
  }, []);

  const cargarVehiculos = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.LOGISTICA.FLOTAS);
      setVehiculos(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error al cargar flotas:", error);
      setVehiculos([]);
    }
  };

  const handleOpenDialog = (vehiculo = null) => {
    if (vehiculo) {
      setEditando(true);
      setVehiculoActual({
        ...vehiculo,
        capacidad_carga: parseFloat(vehiculo.capacidad),
      });
    } else {
      setEditando(false);
      setVehiculoActual({
        placa: "",
        tipo: "",
        marca: "",
        modelo: "",
        año: "",
        color: "",
        capacidad_carga: "",
        capacidad_volumen: "",
        kilometraje: "",
        estado: "activo",
        fecha_adquisicion: "",
        valor_adquisicion: "",
        ultimo_mantenimiento: "",
        proximo_mantenimiento: "",
        observaciones: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditando(false);
    setTabValue(0);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setVehiculoActual({ ...vehiculoActual, [name]: value });
  };

  const handleGuardar = async () => {
    try {
      if (editando) {
        await axios.put(`${API_ENDPOINTS.LOGISTICA.FLOTAS}/${vehiculoActual.id}`, vehiculoActual);
      } else {
        await axios.post(API_ENDPOINTS.LOGISTICA.FLOTAS, vehiculoActual);
      }
      handleCloseDialog();
      cargarVehiculos();
    } catch (error) {
      console.error("Error al guardar vehículo:", error);
    }
  };

  const handleEliminar = async (id) => {
    if (window.confirm("¿Está seguro de eliminar este vehículo?")) {
      try {
        await axios.delete(`${API_ENDPOINTS.LOGISTICA.FLOTAS}/${id}`);
        cargarVehiculos();
      } catch (error) {
        console.error("Error al eliminar vehículo:", error);
      }
    }
  };

  const handleRegistrarMantenimiento = (id) => {
    console.log("Registrar mantenimiento para vehículo:", id);
    // Aquí se abriría un dialog específico para registrar mantenimiento
  };

  const getEstadoChip = (estado) => {
    const est = estadosVehiculo.find((e) => e.value === estado.toLowerCase()) || estadosVehiculo[0];
    return <Chip label={est.label} color={est.color} size="small" />;
  };

  const getTipoIcon = (tipo) => {
    const icons = {
      Camión: "local_shipping",
      Camioneta: "directions_car",
      Van: "airport_shuttle",
      Moto: "two_wheeler",
      Trailer: "rv_hookup",
    };
    return icons[tipo] || "directions_car";
  };

  /* eslint-disable react/prop-types */
  const columns = [
    {
      Header: "Vehículo",
      accessor: "vehiculo",
      width: "20%",
      Cell: ({ row }) => (
        <MDBox display="flex" alignItems="center" gap={1}>
          <Icon fontSize="medium">{getTipoIcon(row.original.tipo)}</Icon>
          <MDBox>
            <MDTypography variant="button" fontWeight="bold">
              {row.original.placa}
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block">
              {row.original.marca} {row.original.modelo}
            </MDTypography>
          </MDBox>
        </MDBox>
      ),
    },
    { Header: "Tipo", accessor: "tipo", width: "10%" },
    { Header: "Año", accessor: "año", width: "8%" },
    { Header: "Capacidad", accessor: "capacidad", width: "10%" },
    {
      Header: "Kilometraje",
      accessor: "kilometraje",
      width: "10%",
      Cell: ({ value }) => `${value.toLocaleString()} km`,
    },
    { Header: "Conductor", accessor: "conductor_actual", width: "12%" },
    { Header: "Ruta Actual", accessor: "ruta_actual", width: "10%" },
    {
      Header: "Estado",
      accessor: "estado",
      width: "10%",
      Cell: ({ value }) => getEstadoChip(value),
    },
    {
      Header: "Acciones",
      accessor: "acciones",
      width: "10%",
      Cell: ({ row }) => (
        <MDBox display="flex" gap={1}>
          <Tooltip title="Mantenimiento">
            <IconButton
              size="small"
              color="warning"
              onClick={() => handleRegistrarMantenimiento(row.original.id)}
            >
              <Icon fontSize="small">build</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton size="small" color="info" onClick={() => handleOpenDialog(row.original)}>
              <Icon fontSize="small">edit</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton size="small" color="error" onClick={() => handleEliminar(row.original.id)}>
              <Icon fontSize="small">delete</Icon>
            </IconButton>
          </Tooltip>
        </MDBox>
      ),
    },
  ];
  /* eslint-enable react/prop-types */

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <MDTypography variant="h5">Flotas y Vehículos</MDTypography>
        <MDButton
          variant="gradient"
          color="info"
          startIcon={<Icon>add</Icon>}
          onClick={() => handleOpenDialog()}
        >
          Nuevo Vehículo
        </MDButton>
      </MDBox>

      {/* Estadísticas Rápidas */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <MDBox p={2} textAlign="center">
              <Icon fontSize="large" color="info">
                local_shipping
              </Icon>
              <MDTypography variant="h4" fontWeight="bold" mt={1}>
                {vehiculos.length}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Total de Vehículos
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <MDBox p={2} textAlign="center">
              <Icon fontSize="large" color="success">
                check_circle
              </Icon>
              <MDTypography variant="h4" fontWeight="bold" mt={1}>
                {vehiculos.filter((v) => v.estado === "activo").length}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Activos
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <MDBox p={2} textAlign="center">
              <Icon fontSize="large" color="warning">
                build
              </Icon>
              <MDTypography variant="h4" fontWeight="bold" mt={1}>
                {
                  vehiculos.filter((v) => v.estado === "mantenimiento" || v.estado === "reparacion")
                    .length
                }
              </MDTypography>
              <MDTypography variant="caption" color="text">
                En Mantenimiento
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <MDBox p={2} textAlign="center">
              <Icon fontSize="large" color="primary">
                attach_money
              </Icon>
              <MDTypography variant="h4" fontWeight="bold" mt={1}>
                ${(vehiculos.reduce((sum, v) => sum + (v.costo_mensual || 0), 0) / 1000).toFixed(1)}
                K
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Costo Mensual Total
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      {/* Tabla de Vehículos */}
      <Card>
        <DataTable
          table={{ columns, rows: vehiculos }}
          showTotalEntries={true}
          isSorted={true}
          noEndBorder
          entriesPerPage={false}
          canSearch
        />
      </Card>

      {/* Dialog para Crear/Editar Vehículo */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editando ? "Editar Vehículo" : "Nuevo Vehículo"}</DialogTitle>
        <DialogContent>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
            <Tab label="Información General" />
            <Tab label="Especificaciones" />
            <Tab label="Mantenimiento" />
          </Tabs>

          {/* Tab 0: Información General */}
          {tabValue === 0 && (
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Placa"
                  name="placa"
                  value={vehiculoActual.placa}
                  onChange={handleInputChange}
                  required
                  placeholder="ABC-123"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Tipo de Vehículo"
                  name="tipo"
                  value={vehiculoActual.tipo}
                  onChange={handleInputChange}
                  required
                >
                  {tiposVehiculo.map((tipo) => (
                    <MenuItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Marca"
                  name="marca"
                  value={vehiculoActual.marca}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Modelo"
                  name="modelo"
                  value={vehiculoActual.modelo}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Año"
                  name="año"
                  value={vehiculoActual.año}
                  onChange={handleInputChange}
                  inputProps={{ min: 1990, max: new Date().getFullYear() + 1 }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Color"
                  name="color"
                  value={vehiculoActual.color}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  select
                  label="Estado"
                  name="estado"
                  value={vehiculoActual.estado}
                  onChange={handleInputChange}
                  required
                >
                  {estadosVehiculo.map((estado) => (
                    <MenuItem key={estado.value} value={estado.value}>
                      {estado.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          )}

          {/* Tab 1: Especificaciones */}
          {tabValue === 1 && (
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Capacidad de Carga (kg)"
                  name="capacidad_carga"
                  value={vehiculoActual.capacidad_carga}
                  onChange={handleInputChange}
                  inputProps={{ min: 0, step: 10 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Capacidad de Volumen (m³)"
                  name="capacidad_volumen"
                  value={vehiculoActual.capacidad_volumen}
                  onChange={handleInputChange}
                  inputProps={{ min: 0, step: 0.1 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Kilometraje Actual"
                  name="kilometraje"
                  value={vehiculoActual.kilometraje}
                  onChange={handleInputChange}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha de Adquisición"
                  name="fecha_adquisicion"
                  value={vehiculoActual.fecha_adquisicion}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Valor de Adquisición"
                  name="valor_adquisicion"
                  value={vehiculoActual.valor_adquisicion}
                  onChange={handleInputChange}
                  inputProps={{ min: 0, step: 100 }}
                  InputProps={{
                    startAdornment: <span style={{ marginRight: 8 }}>$</span>,
                  }}
                />
              </Grid>
            </Grid>
          )}

          {/* Tab 2: Mantenimiento */}
          {tabValue === 2 && (
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Último Mantenimiento"
                  name="ultimo_mantenimiento"
                  value={vehiculoActual.ultimo_mantenimiento}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Próximo Mantenimiento"
                  name="proximo_mantenimiento"
                  value={vehiculoActual.proximo_mantenimiento}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Observaciones"
                  name="observaciones"
                  value={vehiculoActual.observaciones}
                  onChange={handleInputChange}
                  placeholder="Notas adicionales sobre el vehículo..."
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleGuardar} variant="contained" color="info">
            {editando ? "Actualizar" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}
